import {DataFetcher, useGrpcRequest} from '@aboutyou/data-fetcher';
import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {ArticleDetailMetaService_GetPageMetaDataLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/meta/service.grpc.lazy';
import {GetPageMetaDataResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/meta/service.types';
import {Head} from '@aboutyou/head-elements';
import {
  RouterErrorHandler,
  useParams,
  useRecentFullUrl,
} from '@aboutyou/router';
import {DetailPageTracker} from '@aboutyou/tracking';
import React from 'react';
import {useReceivedTrackingData} from '../../../features/ProductTile/hooks/useTileTrackingData';
import {useProductIdFromSlug} from '../../../features/ProductUrl';
import {
  ensure404Page,
  extractMetaAndLinkTags,
  isSeoData,
  setDataTestIds,
} from '../../../shared-deprecated/metaUtils';
import {useEnsureRedirectData} from '../../../shared-deprecated/useEnsureRedirectData';
import {useUpdateTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';
import {useGetBadgeListKeys} from '../utils/useGetBadgeListKeys';
import {useTrackDetailPageWithReductions} from './utils/useTrackDetailPageWithReductions';
import {useTrackProductSelection} from './utils/useTrackProductSelection';

interface Props {
  selectedSize?: SizeSelection_Size;
  isInMiniADPDialog?: boolean;
}

export const PageMetaData: React.FC<Props> = props => {
  return (
    <DataFetcher
      request={useArticleDetailPageMetaData()}
      Error={RouterErrorHandler}
      View={View}
      viewProps={props}
    />
  );
};

export const useArticleDetailPageMetaData = () => {
  const ensureRedirectData = useEnsureRedirectData();
  const fullUrl = useRecentFullUrl();
  const productId = useProductIdFromSlug();
  const {brand} = useParams<{brand?: string}>();

  return useGrpcRequest(
    ArticleDetailMetaService_GetPageMetaDataLazy,
    {
      productId: productId ?? -1, // the fallback value is -1 is to make the protoc-gen-ts compiler happy, we still get a 404 from the backend for productId -1
      brandSlug: brand ?? '', // the fallback value is '' is to make the protoc-gen-ts compiler happy, we still get a correct meta info from the backend for brand '
      url: fullUrl,
    },
    {
      keepPreviousData: false,
      validateData: ensureRedirectData,
      validateError: ensure404Page,
      retry: false,
    },
  );
};

const View: React.FC<GetPageMetaDataResponse & Props> = props => {
  const {
    meta,
    product,
    brand,
    selectedSize,
    recommendedTargetGroup,
    isInMiniADPDialog = false,
  } = props;
  const {isTargetGroupUpdating} = useUpdateTargetGroup(recommendedTargetGroup);
  const {trackingData, isTrackingBlocked} = useReceivedTrackingData({
    productId: product?.id,
  });
  const {badgesListKeys, isFetching: isFetchingBadges} = useGetBadgeListKeys({
    isInMiniADPDialog,
  });

  const {price, shouldTrackViewItemEvent} =
    useTrackDetailPageWithReductions(selectedSize);

  useTrackProductSelection({
    product: product!,
    brand: brand!,
    badges: badgesListKeys,
  });

  if (!isSeoData(meta)) {
    return null;
  }

  const seoData = meta.type.seoData;
  const [metaTags, linkTags] = setDataTestIds(
    extractMetaAndLinkTags(seoData.elements),
  );

  return (
    <>
      {!isTargetGroupUpdating && !isTrackingBlocked && !isFetchingBadges && (
        <DetailPageTracker
          product={product!}
          brand={brand!}
          price={price}
          pageTitle={seoData.title}
          section={trackingData?.recoType || ''}
          shouldTrackViewItemEvent={shouldTrackViewItemEvent}
          badgesList={badgesListKeys}
        />
      )}
      <Head meta={metaTags} link={linkTags}>
        <title data-testid="MetaTitle">{seoData.title}</title>
      </Head>
    </>
  );
};
